const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

/** 1234 -> "1.2K", 1000 -> "1K", 1500000 -> "1.5M", 999 -> "999". */
export function formatCompact(n: number): string {
  return compact.format(n);
}

/* ── An external link that came from data ──
   The founder pipeline stores what it found, and what it found is often a bare
   "linkedin.com/in/someone" with no scheme. A browser reads that as a path, so
   the link opened dev.alisterai.com/miton/linkedin.com/in/someone and looked
   broken. Normalise on the way out; the stored value is left alone. */
export function externalHref(url: string | null | undefined): string | null {
  const raw = (url ?? "").trim();
  if (!raw) return null;
  if (/^(https?:)?\/\//i.test(raw)) return raw.startsWith("//") ? `https:${raw}` : raw;
  if (/^(mailto:|tel:)/i.test(raw)) return raw;
  if (raw.startsWith("/") || raw.startsWith("#")) return null; // a path is not an external link
  return `https://${raw}`;
}

/* ── Numbers on the /miton dashboards ──
   The UI is English, so the numbers are too (docs/brand/dashboard-rules.md):
   37,762 not 37 762, 62% with no space, a real minus sign, ±0 for no change,
   and a percent keeps one decimal only while it is under 10. */
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** 37762 -> "37,762". */
export function fmtInt(n: number): string {
  return whole.format(Math.round(n)).replace(/^-/, "−");
}

/** 0.62 -> "62%", 0.075 -> "7.5%", with `signed` -0.31 -> "−31%". */
export function fmtPct(share: number, signed = false): string {
  const a = Math.abs(share * 100);
  const shown = a < 10 ? a.toFixed(1).replace(/\.0$/, "") : a.toFixed(0);
  const sign = signed ? (share < 0 ? "−" : share > 0 ? "+" : "±") : share < 0 ? "−" : "";
  return `${sign}${shown}%`;
}

/** A count delta: "+3", "−14", "±0". */
export function fmtDelta(n: number): string {
  if (n > 0) return `+${fmtInt(n)}`;
  if (n < 0) return `−${fmtInt(Math.abs(n))}`;
  return "±0";
}

/** "2026-10-26" -> "Mon 26 Oct", the one date shape a meta chip carries. */
export function fmtDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).replace(",", "");
}
